/**
 * Allocation form dialog for New/Edit.
 */
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'

import DateTextField from '../../components/DateTextField'
import NumericInput from './NumericInput'
import { useSnackbar } from '../../hooks/useSnackbar'
import {
  useCreateFuturesAllocationRowMutation,
  useGetFuturesOrderDetailQuery,
  useGetFuturesOrderOptionsQuery,
  useGetLocalPContractOptionsQuery,
  useGetLocalPFutureDetailQuery,
  useGetLocalSContractOptionsQuery,
  useGetLocalSDetailQuery,
  useUpdateFuturesAllocationRowMutation,
} from '../../store/api/futuresControlApi'
import { futuresApiErrorMessage } from './futuresApiErrorMessage'
import {
  futuresFormDialogActionBtnSx,
  futuresFormDialogActionsBoxSx,
  futuresFormDialogTitleSx,
} from './futuresAccessUi'

export type AllocationFormRow = Record<string, unknown> & { id: string }

// Skill `/convert-form` readonly standard: neutral gray (#f1f3f5 light /
// rgba(255,255,255,0.06) dark) thay vì `action.hover` (4% black overlay) —
// trên theme TLC cream nó render phớt hồng, khó phân biệt với editable.
const readonlySx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: 'text.secondary',
    fontWeight: 600,
    cursor: 'default',
  },
} as const

function str(v: unknown): string {
  if (v == null || v === '') return ''
  return String(v)
}

function numLot(v: string): number | undefined {
  if (v.trim() === '') return undefined
  const n = Number(v.replace(/,/g, ''))
  return Number.isFinite(n) ? n : undefined
}

function asFiniteNumber(v: unknown): number {
  if (v == null || v === '') return 0
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''))
  return Number.isFinite(n) ? n : 0
}

function fmtNum(v: unknown): string {
  if (v == null || v === '') return ''
  if (typeof v === 'number' && Number.isFinite(v)) {
    return Number.isInteger(v) ? String(v) : String(v)
  }
  return String(v)
}

/** Access `frmFuturesQryAllocationAdd_noalc`: Left(contract_no,1) = "P" → Contract P else Contract S. */
function contractNoToPS(cn: string): { contractP: string; contractS: string } {
  const t = cn.trim()
  if (!t) return { contractP: '', contractS: '' }
  if (t.startsWith('P')) return { contractP: t, contractS: '' }
  return { contractP: '', contractS: t }
}

export interface FuturesAllocationFormDialogProps {
  open: boolean
  onClose: () => void
  mode: 'new' | 'edit'
  sourceRow?: AllocationFormRow | null
  presetContractNo?: string | null
  presetBalanceLot?: string | null
  canEdit: boolean
}

export default function FuturesAllocationFormDialog({
  open,
  onClose,
  mode,
  sourceRow,
  presetContractNo,
  presetBalanceLot,
  canEdit,
}: FuturesAllocationFormDialogProps) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'
  const creating = !isEdit

  const [date, setDate] = useState('')
  const [contractP, setContractP] = useState('')
  const [contractS, setContractS] = useState('')
  const [futuresId, setFuturesId] = useState('')
  const [lotAllocating, setLotAllocating] = useState('')

  const [notAllocatedLot, setNotAllocatedLot] = useState('')
  const [balanceLot, setBalanceLot] = useState('')
  const [buyOrSell, setBuyOrSell] = useState('')

  const [createRow, createState] = useCreateFuturesAllocationRowMutation()
  const [updateRow, updateState] = useUpdateFuturesAllocationRowMutation()
  const [touched, setTouched] = useState(false)

  const busy = createState.isLoading || updateState.isLoading

  /**
   * Access `btnEdit_Click`: trong Edit mode, `balance_lot` và `not_allocated_lot`
   * lấy từ DLookup rồi **cộng thêm** giá trị `lot_allocating` hiện tại của dòng
   * đang sửa — vì DLookup đã trừ đi phần lot đó rồi, phải cộng lại để cho phép
   * user "edit lên" trong giới hạn capacity ban đầu.
   */
  const editAdjustment = useMemo(() => {
    if (!isEdit || !sourceRow) return 0
    return asFiniteNumber(sourceRow['lotAllocating'])
  }, [isEdit, sourceRow])

  // Option dropdown lists — refetch on mount để pick up contract / futures order
  // mới do user khác vừa tạo (RTK tag invalidation đã cover phần lớn case, đây
  // là safety net cho cross-user concurrent creation).
  const localPOptionsQ = useGetLocalPContractOptionsQuery(undefined, {
    skip: !open,
    refetchOnMountOrArgChange: true,
  })
  const localSOptionsQ = useGetLocalSContractOptionsQuery(undefined, {
    skip: !open,
    refetchOnMountOrArgChange: true,
  })
  const futuresOrderOptionsQ = useGetFuturesOrderOptionsQuery(undefined, {
    skip: !open,
    refetchOnMountOrArgChange: true,
  })

  // Autofill `balance` (P) / `lotToFix` (S) / `notAllocated` + `buyOrSell` (futures
  // order) gọi single-row detail endpoint — BYPASS date filter ở page level. Trước
  // đây lookup từ list query (`useGetFuturesOrdersQuery`/`useGetLocalP/S...Query`)
  // bị BE filter theo dateFrom/dateTo từ user_preferences → ID nằm ngoài range
  // (vd FU-11361) không có trong list, autofill thành rỗng và user nhập sai capacity.
  // `refetchOnMountOrArgChange: true` + `keepUnusedDataFor: 0` → fresh mỗi lần mở
  // dialog hoặc đổi ID, không dùng cache stale.
  const contractPTrim = contractP.trim()
  const contractSTrim = contractS.trim()
  const futuresIdTrim = futuresId.trim()
  const localPDetailQ = useGetLocalPFutureDetailQuery(contractPTrim, {
    skip: !open || !contractPTrim,
    refetchOnMountOrArgChange: true,
  })
  const localSDetailQ = useGetLocalSDetailQuery(contractSTrim, {
    skip: !open || !contractSTrim,
    refetchOnMountOrArgChange: true,
  })
  const futuresOrderDetailQ = useGetFuturesOrderDetailQuery(futuresIdTrim, {
    skip: !open || !futuresIdTrim,
    refetchOnMountOrArgChange: true,
  })
  const optP = localPOptionsQ.data ?? []
  const optS = localSOptionsQ.data ?? []
  const optOrders = futuresOrderOptionsQ.data ?? []

  const reset = useCallback(() => {
    setDate('')
    setContractP('')
    setContractS('')
    setFuturesId('')
    setLotAllocating('')
    setNotAllocatedLot('')
    setBalanceLot('')
    setBuyOrSell('')
    setTouched(false)
  }, [])

  useEffect(() => {
    if (!open) {
      reset()
      return
    }
    const r = sourceRow
    if (creating) {
      const today = new Date().toISOString().slice(0, 10)
      setDate(today)
      const ps = presetContractNo ? contractNoToPS(presetContractNo) : { contractP: '', contractS: '' }
      setContractP(ps.contractP)
      setContractS(ps.contractS)
      setFuturesId('')
      setLotAllocating('')
      setNotAllocatedLot('')
      setBalanceLot(str(presetBalanceLot))
      setBuyOrSell('')
      return
    }
    if (!r) return
    const cid = str(r['contractId']).trim()
    const ps = contractNoToPS(cid)
    setDate(str(r['date']).slice(0, 10))
    setContractP(ps.contractP)
    setContractS(ps.contractS)
    setFuturesId(str(r['futuresId']))
    setLotAllocating(str(r['lotAllocating'] ?? ''))
    setBuyOrSell(str(r['buysell'] ?? r['buyOrSell'] ?? ''))
    setNotAllocatedLot('')
    setBalanceLot(str(r['balance'] ?? ''))
  }, [open, creating, mode, sourceRow, presetContractNo, presetBalanceLot, reset])

  useEffect(() => {
    if (!open) return
    const p = contractP.trim()
    const s = contractS.trim()
    if (p && !s) {
      const data = localPDetailQ.data
      if (!data || str(data.contractNo).trim() !== p) {
        // chưa có data đúng key → để trống tạm thời, không reset nếu vẫn đang fetching
        if (!localPDetailQ.isFetching) setBalanceLot('')
        return
      }
      const baseBalance = asFiniteNumber(data.balance)
      setBalanceLot(fmtNum(baseBalance + editAdjustment))
      return
    }
    if (s && !p) {
      const data = localSDetailQ.data
      if (!data || str(data.contractSale).trim() !== s) {
        if (!localSDetailQ.isFetching) setBalanceLot('')
        return
      }
      const baseBalance = asFiniteNumber(data.lotToFix)
      setBalanceLot(fmtNum(baseBalance + editAdjustment))
      return
    }
    setBalanceLot('')
  }, [
    open,
    contractP,
    contractS,
    localPDetailQ.data,
    localPDetailQ.isFetching,
    localSDetailQ.data,
    localSDetailQ.isFetching,
    editAdjustment,
  ])

  useEffect(() => {
    if (!open) return
    const f = futuresId.trim()
    if (!f) {
      setNotAllocatedLot('')
      setBuyOrSell('')
      return
    }
    const data = futuresOrderDetailQ.data
    if (!data || str(data.orderNumber).trim() !== f) {
      if (!futuresOrderDetailQ.isFetching) {
        setNotAllocatedLot('')
        setBuyOrSell('')
      }
      return
    }
    const baseNotAllocated = asFiniteNumber(data.notAllocated)
    setNotAllocatedLot(fmtNum(baseNotAllocated + editAdjustment))
    setBuyOrSell(str(data.buyOrSell))
  }, [open, futuresId, futuresOrderDetailQ.data, futuresOrderDetailQ.isFetching, editAdjustment])

  // Per-field validation flags — drives the red error highlight on each input
  // (mirrors LocalPFormDialog `error={!!errors.field}` pattern). Combobox fields
  // also flag when the typed value is not in the lookup list.
  const fieldErrors = useMemo(() => {
    const p = contractP.trim()
    const s = contractS.trim()
    const f = futuresId.trim()
    const pSelected = p && optP.includes(p) ? p : ''
    const sSelected = s && optS.includes(s) ? s : ''
    const fSelected = f && optOrders.includes(f) ? f : ''
    const lotN = numLot(lotAllocating)
    const pListInvalid = !!p && !optP.includes(p)
    const sListInvalid = !!s && !optS.includes(s)
    const fListInvalid = !!f && !optOrders.includes(f)
    return {
      date: !date.trim(),
      contractP: pListInvalid || (!pSelected && !sSelected) || (pSelected && sSelected) ? true : false,
      contractS: sListInvalid || (!pSelected && !sSelected) || (pSelected && sSelected) ? true : false,
      futuresId: fListInvalid || !fSelected,
      lotAllocating: lotN === undefined,
    }
  }, [date, contractP, contractS, futuresId, lotAllocating, optP, optS, optOrders])

  const validationError = useMemo(() => {
    if (!open) return null
    const p = contractP.trim()
    const s = contractS.trim()
    const f = futuresId.trim()
    const pSelected = p && optP.includes(p) ? p : ''
    const sSelected = s && optS.includes(s) ? s : ''
    const fSelected = f && optOrders.includes(f) ? f : ''
    const lotN = numLot(lotAllocating)
    if (!date.trim()) return 'Please choose Date'
    if (p && !optP.includes(p)) return 'Please choose Contract P from list!'
    if (s && !optS.includes(s)) return 'Please choose Contract S from list!'
    if (!pSelected && !sSelected) return 'Please choose Contract No!'
    if (pSelected && sSelected) return 'Please choose Only one Contract No!'
    if (f && !optOrders.includes(f)) return 'Please choose Futures ID from list!'
    if (!fSelected) return 'Please choose Futures ID!'
    if (lotN === undefined) return 'Please type Lot allocating!'
    const bs = buyOrSell.trim().toUpperCase()
    const isCheck = !((pSelected && bs === 'BUY') || (sSelected && bs === 'SELL'))
    if (isCheck) {
      const bal = Number(String(balanceLot).replace(/,/g, ''))
      if (Number.isFinite(bal) && lotN > bal) return 'Please check Lot allocating <= Balance lot!'
      const na = Number(String(notAllocatedLot).replace(/,/g, ''))
      if (Number.isFinite(na) && lotN > na) return 'Please check Lot allocating <= Not Allocated Lot!'
    }
    return null
  }, [open, date, contractP, contractS, futuresId, lotAllocating, buyOrSell, balanceLot, notAllocatedLot, optP, optS, optOrders])

  const handleSave = async () => {
    setTouched(true)
    if (validationError) {
      snack.error(validationError)
      return
    }
    const p = contractP.trim()
    const s = contractS.trim()
    const f = futuresId.trim()
    const contractId = optP.includes(p) ? p : optS.includes(s) ? s : ''
    const lot = numLot(lotAllocating)
    if (lot === undefined || !contractId || !optOrders.includes(f)) return
    try {
      if (creating) {
        await createRow({
          date,
          contractId,
          futuresId: f,
          lotAllocating: lot,
        }).unwrap()
      } else {
        await updateRow({
          date,
          contractId,
          futuresId: f,
          lotAllocating: lot,
        }).unwrap()
      }
      snack.success('Saved.')
      onClose()
    } catch (err) {
      snack.error(futuresApiErrorMessage(err))
    }
  }

  const dialogTitle = mode === 'edit' ? 'Futures Control - Allocation (Edit)' : 'Futures Control - Allocation (New)'
  const lockKeys = isEdit

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(720px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={futuresFormDialogTitleSx}>
        <Typography component="span" sx={{ flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}>
          {dialogTitle}
        </Typography>
        <Box sx={futuresFormDialogActionsBoxSx}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={busy || !canEdit}
            sx={futuresFormDialogActionBtnSx}
          >
            {busy ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button variant="outlined" size="small" onClick={onClose} sx={futuresFormDialogActionBtnSx}>
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{
          pt: 0.75,
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          '& .MuiInputBase-root': {
            minHeight: 32,
            fontSize: '0.8125rem',
          },
          '& .MuiInputBase-input': {
            py: 0.5,
            fontSize: '0.8125rem',
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.8125rem',
            fontWeight: 500,
          },
          '& .MuiInputLabel-shrink': {
            fontWeight: 600,
          },
        }}
      >
        <Box sx={{ maxWidth: 720, mx: 'auto', pb: 1.5 }}>
          <Grid container spacing={0.75}>
            <Grid item xs={12}>
              <DateTextField
                label="Date *"
                size="small"
                fullWidth
                value={date}
                onChange={(v) => setDate(v)}
                disabled={lockKeys || !canEdit}
                autoFocus={creating && canEdit}
                error={touched && fieldErrors.date}
                helperText={touched && fieldErrors.date ? 'Please choose Date' : undefined}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                autoHighlight
                options={optP}
                loading={localPOptionsQ.isLoading || localPOptionsQ.isFetching}
                value={contractP || null}
                disabled={!canEdit || lockKeys}
                isOptionEqualToValue={(opt, val) => opt === val}
                onChange={(_, v) => {
                  const s = typeof v === 'string' ? v : (v ?? '')
                  setContractP(s)
                  setContractS('')
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Contract P *"
                    size="small"
                    fullWidth
                    error={touched && fieldErrors.contractP}
                    helperText={touched && fieldErrors.contractP ? 'Please choose Contract P from list' : undefined}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                autoHighlight
                options={optS}
                loading={localSOptionsQ.isLoading || localSOptionsQ.isFetching}
                value={contractS || null}
                disabled={!canEdit || lockKeys}
                isOptionEqualToValue={(opt, val) => opt === val}
                onChange={(_, v) => {
                  const s = typeof v === 'string' ? v : (v ?? '')
                  setContractS(s)
                  setContractP('')
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Contract S *"
                    size="small"
                    fullWidth
                    error={touched && fieldErrors.contractS}
                    helperText={touched && fieldErrors.contractS ? 'Please choose Contract S from list' : undefined}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                * Only choose one contract: Contract P or Contract S
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Balance lot"
                size="small"
                fullWidth
                value={balanceLot}
                disabled
                sx={readonlySx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                autoHighlight
                options={optOrders}
                loading={futuresOrderOptionsQ.isLoading || futuresOrderOptionsQ.isFetching}
                value={futuresId || null}
                disabled={!canEdit || lockKeys}
                isOptionEqualToValue={(opt, val) => opt === val}
                onChange={(_, v) => {
                  setFuturesId(typeof v === 'string' ? v : (v ?? ''))
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Futures ID *"
                    size="small"
                    fullWidth
                    error={touched && fieldErrors.futuresId}
                    helperText={touched && fieldErrors.futuresId ? 'Please choose Futures ID from list' : undefined}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="Buy/Sell" size="small" fullWidth value={buyOrSell} disabled sx={readonlySx} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Not Allocated lot"
                size="small"
                fullWidth
                value={notAllocatedLot}
                disabled
                sx={readonlySx}
              />
            </Grid>

            <Grid item xs={12}>
              <NumericInput
                label="Lot Allocating *"
                size="small"
                fullWidth
                value={lotAllocating}
                onChange={setLotAllocating}
                disabled={!canEdit}
                error={touched && fieldErrors.lotAllocating}
                helperText={touched && fieldErrors.lotAllocating ? 'Please type Lot allocating' : undefined}
              />
            </Grid>

          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
